import { Chip } from "@mui/material";
import { ChipWrap } from "components/ChipWrap";
import { Box } from "@mui/system";
import { useLogin } from "providers/Login";
import { useNotification } from "providers/Notification";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import { AddDataType } from "./_components/AddDataType";
import Visibility from "@mui/icons-material/Visibility";
import { FormDrawer } from "components/FormDrawer";
import { ListPageHeader } from "components/ListPageHeader";
import { ConfirmDialog } from "components/ConfirmDialog";
import DataTable, { Column } from "components/Table";
import { useResourceList } from "hooks/useResourceList";
import { useDeleteDialog } from "hooks/useDeleteDialog";
import { useFormDrawer } from "hooks/useFormDrawer";
import Circle from "@mui/icons-material/Circle";
import { formatDateTime, fromUnix } from "helpers/dateFormat";
import { useRole } from "hooks/useRole";

interface ObfuscationsState {
    id: string
    name: string
}

interface DataTypeListStateItem {
    id: string
    name: string
    internal: boolean
    disabled: boolean
    description: string
    updatedAt: Date
    obfuscations?: ObfuscationsState[]
}

type columnsType = "internal" | "type" | "name" | "disabled" | "usedby" | "description" | "updatedAt";

const defaultColumns: Column<columnsType>[] = [
    { columnId: "name", width: "15%", direction: "asc", sortOrder: 1 },
    { columnId: "type", width: "15%" },
    { columnId: "disabled", width: "10%" },
    { columnId: "internal", width: "10%" },
    { columnId: "usedby", width: "15%" },
    { columnId: "description", width: "30%" },
    { columnId: "updatedAt", width: "30%" },
];

function toDataType(x: any): DataTypeListStateItem {
    return {
        id: x.type,
        name: x.name,
        internal: x.internal,
        disabled: x.disabled !== undefined ? x.disabled : false,
        description: x.description,
        updatedAt: fromUnix(x.updatedAt),
        obfuscations: x.obfuscations && x.obfuscations.map((item: any) => ({
            id: item.id,
            name: item.name,
        })),
    };
}

export default function DataTypes() {
    const notification = useNotification();
    const login = useLogin();
    const { canEditPolicy } = useRole();
    const list = useResourceList<DataTypeListStateItem, columnsType>({
        url: "/api/datatypes",
        columnsKey: "dataGrid_dataTypeTable_columns_v3",
        defaultColumns,
        mapItem: toDataType,
        paginated: true,
        sorted: true,
        searchable: true,
    });

    const drawer = useFormDrawer();
    const del = useDeleteDialog({
        url: "/api/datatypes",
        label: "Data Type",
        onDeleted: list.reload,
    });

    const setDataTypeDisabled = async (typeId: string, disabled: boolean) => {
        const req = login.GetAxios();

        if (!req) {
            return; //not logged in, nothing to do
        }

        try {
            await req.put(`/api/datatypes/${typeId}/disable`, { disable: disabled });
            list.reload();
            notification.Display({
                type: "success",
                title: "Success",
                message: `Data Type ${disabled ? "disabled" : "enabled"} successfully`
            });
        } catch (err) {
            notification.Display({
                title: "Error",
                message: `Could not ${disabled ? "disable" : "enable"} data type`,
                type: "error",
            });
        }
    };


    return <>
        <Box display="flex" flexDirection="column">
            <ListPageHeader
                title="Data Types"
                search={list.search}
                onSearchChange={list.setSearch}
                searchAllFields={list.searchAllFields}
                onSearchAllFieldsChange={list.setSearchAllFields}
                actionId="new-data-type-button"
                actionLabel="Add New Data Type"
                // NS-753
                onAction={canEditPolicy ? drawer.add : undefined}
            />

            <DataTable
                {...list.tableProps}
                columns={{
                    internal: {
                        displayName: "Internal",
                        sortable: true,
                        render: (x) => {
                            return x.internal ? "YES" : "NO";
                        },
                    },
                    type: {
                        displayName: "Type",
                        sortable: true,
                        render: (x) => {
                            return x.id;
                        },
                    },
                    disabled: {
                        displayName: "Status",
                        sortable: true,
                        render: (x) => {
                            return !x.disabled
                                ? <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                }}>
                                    <Circle sx={{ transform: "scale(0.6)" }} fontSize="small" color="success" /><span>Active</span>
                                </Box>
                                : <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                }}>
                                    <Circle sx={{ transform: "scale(0.6)" }} fontSize="small" color="error" /><span>Inactive</span>
                                </Box>;
                        },
                    },
                    name: {
                        displayName: "Name",
                        sortable: true,
                        render: (x) => {
                            return x.name;
                        },
                    },
                    usedby: {
                        displayName: "Used By Obfuscation(s)",
                        render: (x) => {
                            return <ChipWrap>
                                {(x.obfuscations && x.obfuscations.map(y => <Chip
                                    label={y.name.trim()}
                                    variant="outlined"
                                    size="small"
                                    color={!x.disabled ? "primary" : "default"}
                                    sx={{ opacity: !x.disabled ? 1 : 0.5 }}
                                />))}
                            </ChipWrap>;
                        },
                    },
                    description: {
                        displayName: "Description",
                        render: (x) => {
                            return x.description;
                        },
                    },
                    updatedAt: {
                        displayName: "Last Update",
                        sortable: true,
                        render: (x) => {
                            return formatDateTime(x.updatedAt);
                        },
                    },

                }}
                rowOptionsMenu={[
                    {
                        isVisible: () => canEditPolicy,
                        icon: (x) => !x.disabled ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />,
                        text: (x) => !x.disabled ? "Disable" : "Enable",
                        onClick: (x) => {
                            setDataTypeDisabled(x.id, !x.disabled);
                        }
                    },
                    {
                        isVisible: (x) => !x.internal,
                        icon: (x) => x.internal ? <Visibility fontSize="small" /> : <EditIcon className="edit-button" fontSize="small" />,
                        text: (x) => x.internal || !canEditPolicy ? "View" : "Edit",
                        onClick: (x) => {
                            drawer.edit(x.id);
                        }
                    },
                    {
                        icon: <DeleteIcon className="delete-button" fontSize="small" />,
                        text: "Delete",
                        isVisible: (x) => !x.internal && canEditPolicy,
                        onClick: (x) => {
                            if (x.internal) {
                                return notification.Display({
                                    type: "error",
                                    title: "Validation Error",
                                    message: "Can't delete an internal Data Type",
                                });
                            }

                            if (x.obfuscations && x.obfuscations.length > 0) {
                                return notification.Display({
                                    type: "error",
                                    title: "Validation Error",
                                    message: `Cannot delete ${x.name} because it is in use by ${x.obfuscations.length === 1 ? "an Obfuscation" : "some Obfuscations"} (${x.obfuscations.map(i => i.name).join(", ")})`,
                                });
                            }

                            del.ask(x.name, x.id);
                        }
                    },
                ]}
            />
        </Box>

        <ConfirmDialog {...del.dialogProps} title="Delete DataType" />

        <FormDrawer {...drawer.drawerProps}>
            <AddDataType
                type={drawer.editingId}
                onExit={drawer.close}
                onSave={() => { list.reload(); drawer.close(); }}
            />
        </FormDrawer>
    </>;
}


